// chat/Chat.tsx — the chat panel UI (cloned from Oriora Expense's src/chat/Chat.tsx). Purely ADDITIVE:
// it drives the SAME capabilities the app already has (log / delete / filter / PRs / summarize) by
// typing. All styling is namespaced under .owc-* (Oriora Workout Chat) so it can't collide with the
// existing app CSS (.row / .card / .btn / etc.).
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm tap
// (a chat message can't destroy data on its own) — mirrors Expense's discipline.

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { findNamedMatches } from "./fuzzy";
import { cloudBackupStatus } from "../brain";
import CallOverlay from "../live/CallOverlay";
import { platform } from "../tauri-web";
import { executeAction, type ChatAction, type ResultRow } from "./actions";

const STORE_KEY = "oriora.workout.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    "Tell me what to do — I'll log it, find it, or summarize it, no tapping.\n\n" +
    'e.g. "log bench 100 x 5" · "start push day" · "show workouts this week" · "summarize my last 14 days".',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

const isDestructive = (a: ChatAction) => a.kind === "delete_workout" || a.kind === "delete_set" || a.kind === "delete_routine" || a.kind === "backup_restore" || a.kind === "import_file";
const warnFor = (action: ChatAction): string =>
  action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup — tap Confirm to proceed, or Cancel.`
      : `Delete ${("label" in action && action.label) ? action.label : "this"}? This can't be undone — tap Confirm, or Cancel to keep it.`;
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };

export default function Chat({ onChanged, onOpenProgramFlow, onRepeatLastWorkout, onStartRoutine }: { onChanged?: () => void; onOpenProgramFlow?: () => void; onRepeatLastWorkout?: () => Promise<import("../store").Workout | null>; onStartRoutine?: (id: string) => Promise<import("../store").Routine | null> }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a workout. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // `save_to_cloud`'s "conversation" option needs the chat text — passed to the executor as ctx.
  const transcriptOf = (t: ChatTurn[]): string => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  // Run actions in order until the first destructive one, returned as a hold with its tail.
  async function runQueue(queue: ChatAction[], ctx?: { transcript?: string; openProgramFlow?: () => void; repeatLastWorkout?: () => Promise<import("../store").Workout | null>; startRoutine?: (id: string) => Promise<import("../store").Routine | null> }): Promise<{ lines: string[]; rows?: ResultRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: ResultRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (isDestructive(action)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, ctx);
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  // A photo turn is filed ON-DEVICE onto one of the user's workouts (e.g. a form/progress shot): no
  // model call, no cost. If the message names a workout we fuzzy-match it; if not, the photo goes on
  // the LATEST workout (the one just logged). Ambiguous → we ask which one.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const workouts = await store.listWorkouts(50000);
      if (workouts.length === 0) {
        setTurns([...next, { role: "assistant", content: "Log a workout first, then I'll attach the photo to it." }]);
        return; // keep the photo attached
      }
      const q = text.trim();
      const hits = q ? findNamedMatches(workouts, q) : [];
      // No text → the latest workout (listWorkouts is newest-first); named → fuzzy match.
      let target: (typeof workouts)[number] | null = q ? (hits.length === 1 ? hits[0] : null) : workouts[0];
      if (q && hits.length > 1) {
        setTurns([...next, { role: "assistant", content: `Which workout is this photo for?\n${hits.slice(0, 6).map((w) => `• ${w.name}`).join("\n")}` }]);
        return; // keep the photo attached
      }
      if (q && hits.length === 0) {
        setTurns([...next, { role: "assistant", content: `I couldn't find a workout matching “${q}”. Try its name, or just send to attach it to your latest.` }]);
        return; // keep the photo attached
      }
      await store.setWorkoutPhoto(target!.id, photo!);
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Attached your photo to “${target!.name}”.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, { transcript: transcriptOf(next), openProgramFlow: onOpenProgramFlow, repeatLastWorkout: onRepeatLastWorkout, startRoutine: onStartRoutine });
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: ResultRow[] | undefined;
      const result = await executeAction(action);
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, { transcript: transcriptOf(turns), openProgramFlow: onOpenProgramFlow, repeatLastWorkout: onRepeatLastWorkout, startRoutine: onStartRoutine });
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : " Tell me what else to do.") }]);
  }

  return (
    <div className="owc-chat">
      <div className="owc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`owc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`owc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <ResultRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="owc-msgrow"><div className="owc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="owc-composer owc-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="owc-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the workout, or just send for your latest…" : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. */}
            {platform() === "ios" && <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position sheet,
                so it renders correctly anywhere in the tree, and this needs no structural analysis
                of each app's JSX — a button whose overlay never got mounted is the half-wired
                failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function ResultRows({ rows }: { rows: ResultRow[] }) {
  return (
    <div className="owc-rows">
      {rows.map((r) => (
        <div key={r.id} className="owc-rowitem">
          <span>{r.title} <span className="muted">· {r.subtitle}</span></span>
          {r.right && <span className="tnum" style={{ fontWeight: 600 }}>{r.right}</span>}
        </div>
      ))}
    </div>
  );
}
